import { useReactiveVar } from '@apollo/client';
import { useEffect } from 'react';

import { LocalStorageKey } from '@neo.tax/shared/enums';
import { AuthenticationState, ToastConfigState } from '@neo.tax/shared/state';

/**
 * Checks for IP whitelist error message in localStorage and displays a toast.
 * This handles the case where a user's IP was blocked and they were logged out.
 * The error message is stored in localStorage before logout and displayed here.
 *
 * The hook re-runs when authentication state changes to handle the case where
 * the user logs in and is immediately logged out due to IP whitelist blocking.
 * A small delay ensures the toast is pushed after logout cleanup completes.
 */
export const useIpWhitelistError = (): void => {
  const {
    authenticationServiceState: { authenticationUser, loading },
  } = useReactiveVar(AuthenticationState.authenticationStateVar);

  useEffect(() => {
    // Only check when not loading and user is logged out
    if (loading || authenticationUser) {
      return;
    }

    const errorMessage = localStorage.getItem(LocalStorageKey.PERSIST_IP_WHITELIST_ERROR);
    if (errorMessage) {
      // Delay to ensure toast is pushed after logout cleanup (clearReactiveVars)
      const timeoutId = setTimeout(() => {
        localStorage.removeItem(LocalStorageKey.PERSIST_IP_WHITELIST_ERROR);
        ToastConfigState.pushErrorToast(errorMessage);
      }, 100);

      return (): void => clearTimeout(timeoutId);
    }
  }, [authenticationUser, loading]);
};
